import * as Dialog from '@radix-ui/react-dialog'
import type { ReactNode } from 'react'
import { cn } from '@/utils/cn'
import { CloseIcon } from '@/components/icons'

/**
 * 浮层（抽屉与弹窗）—— 基于 `@radix-ui/react-dialog`。
 *
 * AGENTS.md 9：这类组件不该自己实现。一个「正确」的对话框要处理焦点陷阱、
 * 打开时把焦点移进去、关闭时还给触发元素、Esc 关闭、点遮罩关闭、锁住 body
 * 滚动、`aria-modal` 与标题关联、以及 iOS 上的滚动穿透 —— 每一条都是独立的
 * 坑，Radix 已经全部处理好，自己写只会漏掉几条。
 *
 * 这里只负责两件事：套上站点的视觉（token 化的配色、圆角、动画），
 * 以及把「抽屉」和「弹窗」两种形态收敛成两个语义清楚的组件。
 */

const OVERLAY_CLASS =
  'fixed inset-0 z-50 animate-fade-in bg-navy/50 backdrop-blur-[2px] data-[state=closed]:animate-none'

export interface DrawerProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  /** 无障碍名称。会渲染成可见标题，除非 `hideTitle`。 */
  title: string
  description?: string
  /** 从哪一侧滑出：移动端导航抽屉从左，筛选面板从下方更顺手。 */
  side?: 'left' | 'bottom'
  /** 自定义标题栏。传了就替换默认的「标题 + 关闭按钮」。 */
  header?: ReactNode
  children: ReactNode
}

export function Drawer({
  open,
  onOpenChange,
  title,
  description,
  side = 'left',
  header,
  children,
}: DrawerProps) {
  return (
    <Dialog.Root open={open} onOpenChange={onOpenChange}>
      <Dialog.Portal>
        <Dialog.Overlay className={OVERLAY_CLASS} />
        <Dialog.Content
          className={cn(
            'fixed z-50 flex flex-col overflow-hidden bg-surface-raised shadow-overlay outline-none',
            side === 'left'
              ? 'inset-y-0 left-0 w-[19rem] max-w-[85vw] animate-slide-in-left'
              : 'inset-x-0 bottom-0 max-h-[85vh] animate-slide-up rounded-t-panel',
          )}
        >
          {header ?? (
            <div className="flex items-center justify-between gap-3 border-b border-border px-4 py-3">
              <Dialog.Title className="text-sm font-semibold text-foreground">{title}</Dialog.Title>
              <Dialog.Close
                aria-label="关闭"
                className="-mr-1 rounded-lg p-2 text-muted-foreground transition-colors hover:bg-surface-muted hover:text-foreground"
              >
                <CloseIcon className="size-4" />
              </Dialog.Close>
            </div>
          )}
          {/* header 自定义时标题仍需存在，否则 Radix 会警告缺少无障碍名称。 */}
          {header && <Dialog.Title className="sr-only">{title}</Dialog.Title>}
          {description ? (
            <Dialog.Description className="sr-only">{description}</Dialog.Description>
          ) : null}
          <div className="flex-1 overflow-y-auto overscroll-contain">{children}</div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  )
}

export interface ModalProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  title: string
  description?: string
  children?: ReactNode
  footer?: ReactNode
}

export function Modal({ open, onOpenChange, title, description, children, footer }: ModalProps) {
  return (
    <Dialog.Root open={open} onOpenChange={onOpenChange}>
      <Dialog.Portal>
        <Dialog.Overlay className={OVERLAY_CLASS} />
        <Dialog.Content className="fixed left-1/2 top-1/2 z-50 w-[calc(100vw-2rem)] max-w-md -translate-x-1/2 -translate-y-1/2 animate-slide-up rounded-panel border border-border bg-surface-raised p-5 shadow-overlay outline-none">
          <div className="flex items-start justify-between gap-4">
            <div className="space-y-1">
              <Dialog.Title className="text-base font-semibold text-foreground">
                {title}
              </Dialog.Title>
              {description && (
                <Dialog.Description className="text-sm leading-6 text-muted-foreground">
                  {description}
                </Dialog.Description>
              )}
            </div>
            <Dialog.Close
              aria-label="关闭"
              className="-mr-1 -mt-1 rounded-lg p-2 text-muted-foreground transition-colors hover:bg-surface-muted hover:text-foreground"
            >
              <CloseIcon className="size-4" />
            </Dialog.Close>
          </div>
          {children && <div className="mt-4">{children}</div>}
          {footer && <div className="mt-5 flex justify-end gap-2">{footer}</div>}
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  )
}
